import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Clock3,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import {
  activityStatusLabel,
  activityStatusTone,
  formatInvestorProfitDisplay,
  formatPlayActivityAt,
  formatPlayDateTime,
  getInvestorSharePercent,
  hasRoundContext,
  investorOutcomeLabel,
} from "../../utils/bondPlayActivityHelpers";
import { formatCoinFlipCoin, sideLabel } from "../../utils/coinFlipHelpers";
import { formatBondDiceCoin, faceLabel, pickLabel } from "../../utils/bondDiceHelpers";
import { formatBondNumbersCoin, numberLabel, pickLabel as numbersPickLabel } from "../../utils/bondNumbersHelpers";

const SummaryRow = ({ label, value, valueClass = "" }) => (
  <div className="bdraw-investor-summary-row">
    <span className="bdraw-investor-summary-label">{label}</span>
    <strong className={valueClass}>{value}</strong>
  </div>
);

const InvestorProfitCard = ({ recent, formatCoin }) => {
  const profit = Number(recent.profit || 0);
  const positive = profit >= 0;

  return (
    <div className={`bdraw-investor-profit${positive ? " won" : " lost"}`}>
      <div className="bdraw-investor-profit-head">
        {positive ? (
          <TrendingUp size={15} strokeWidth={2.5} />
        ) : (
          <TrendingDown size={15} strokeWidth={2.5} />
        )}
        <span>{investorOutcomeLabel(profit, recent.status)}</span>
      </div>
      <strong className={positive ? "win" : "loss"}>
        {formatInvestorProfitDisplay(profit, formatCoin)}
      </strong>
    </div>
  );
};

const FlipRoundContext = ({ game }) => (
  <div className="bdraw-player-metrics bdraw-investor-round-context">
    <div className="bdraw-player-metric">
      <span className="bdraw-player-metric-label">Player pick</span>
      <strong>{sideLabel(game.playerSide)}</strong>
    </div>
    <div className="bdraw-player-metric">
      <span className="bdraw-player-metric-label">Outcome</span>
      <strong>{sideLabel(game.outcome)}</strong>
    </div>
  </div>
);

const DiceRoundContext = ({ game }) => {
  const pick = game.playerPick ?? (game.playerFace != null ? String(game.playerFace) : null);
  return (
    <div className="bdraw-player-metrics bdraw-investor-round-context">
      <div className="bdraw-player-metric">
        <span className="bdraw-player-metric-label">Player pick</span>
        <strong>{pickLabel(pick)}</strong>
      </div>
      <div className="bdraw-player-metric">
        <span className="bdraw-player-metric-label">Rolled</span>
        <strong>{faceLabel(game.outcome)}</strong>
      </div>
    </div>
  );
};

const InvestorGameHistoryCard = ({ game, formatCoin, gameType, RoundContext }) => {
  const profit = Number(game.profit || 0);
  const positive = profit >= 0;

  return (
    <div className="bdraw-investor-history-card">
      <div className="bdraw-investor-history-head">
        <span className="bdraw-investor-history-date">
          {formatPlayDateTime(game.date, game.time) ||
            formatPlayActivityAt(game.settledAt) ||
            "—"}
        </span>
        <span className={`bdraw-player-status ${activityStatusTone(game.status)}`}>
          {activityStatusLabel(game.status)}
        </span>
      </div>
      <div className="bdraw-investor-history-body">
        {RoundContext && hasRoundContext(game, gameType) ? (
          <RoundContext game={game} />
        ) : null}
        <SummaryRow label="Investment" value={formatCoin(game.investment)} />
        <SummaryRow label="Pool Size" value={formatCoin(game.poolSize)} />
        <SummaryRow label="Your Share" value={`${getInvestorSharePercent(game)}%`} valueClass="share" />
        {Number(game.used || 0) || Number(game.refunded || 0) ? (
          <SummaryRow
            label="Used / Refunded"
            value={`${formatCoin(game.used || 0)} / ${formatCoin(game.refunded || 0)}`}
          />
        ) : null}
        <div className="bdraw-investor-history-result">
          <span className="bdraw-investor-summary-label">{investorOutcomeLabel(profit, game.status)}</span>
          <strong className={positive ? "win" : "loss"}>
            {formatInvestorProfitDisplay(profit, formatCoin)}
          </strong>
        </div>
      </div>
    </div>
  );
};

const NumbersRoundContext = ({ game }) => (
  <div className="bdraw-player-metrics bdraw-investor-round-context">
    <div className="bdraw-player-metric">
      <span className="bdraw-player-metric-label">Player pick</span>
      <strong>{numbersPickLabel(game.playerPick)}</strong>
    </div>
    <div className="bdraw-player-metric">
      <span className="bdraw-player-metric-label">Ref → Reveal</span>
      <strong>
        {numberLabel(game.referenceNumber)} → {numberLabel(game.outcome)}
      </strong>
    </div>
  </div>
);

const BondPlayInvestorCard = ({ investor, game = "flip", isCurrentUser = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { recent, recentGames = [] } = investor;
  const avatarUrl = generateAvatarUrl(investor.name || "Investor");
  const active = Number(investor.active || 0);
  const pending = Number(investor.pending || 0);
  const profit = Number(recent?.profit || 0);
  const formatCoin =
    game === "dice" ? formatBondDiceCoin : game === "numbers" ? formatBondNumbersCoin : formatCoinFlipCoin;
  const RoundContext =
    game === "dice" ? DiceRoundContext : game === "numbers" ? NumbersRoundContext : FlipRoundContext;
  const lastPnlLabel = investorOutcomeLabel(profit, recent?.status);
  const historyGames = recentGames.slice(recent ? 1 : 0, 3);

  return (
    <li className={`bdraw-player-card bdraw-investor-card${expanded ? " expanded" : ""}${isCurrentUser ? " mine" : ""}`}>
      <button
        type="button"
        className="bdraw-player-card-toggle"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <img className="bdraw-player-avatar" src={avatarUrl} alt="" />
        <span className="bdraw-player-head-main">
          <span className="bdraw-player-name-row">
            <strong>{investor.name || "Anonymous"}</strong>
            {isCurrentUser ? <span className="bdraw-player-you">YOU</span> : null}
          </span>
          <span className="bactivity-card-subtitle">
            {recent ? lastPnlLabel : investor.activeStatus ? "Backing new tickets" : "Paused"}
          </span>
        </span>
        <span className="bdraw-investor-head-stats">
          <span className={`bdraw-investor-stat${investor.activeStatus ? " on" : " paused"}`} title="Active balance">
            <Wallet size={11} strokeWidth={2.5} />
            {formatCoin(active)}
          </span>
          <span className="bdraw-investor-stat pending" title="Held in reserve">
            <Clock3 size={11} strokeWidth={2.5} />
            {formatCoin(pending)}
          </span>
          <span className={`bdraw-investor-stat ${profit >= 0 ? "won" : "lost"}`} title="Last round P/L">
            {profit >= 0 ? (
              <TrendingUp size={11} strokeWidth={2.5} />
            ) : (
              <TrendingDown size={11} strokeWidth={2.5} />
            )}
            {formatInvestorProfitDisplay(profit, formatCoin)}
          </span>
        </span>
        <span className="bdraw-player-chevron" aria-hidden>
          {expanded ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
        </span>
      </button>

      {expanded ? (
        <div className="bdraw-player-card-body">
          {recent ? (
            <>
              <div className="bdraw-player-meta-row">
                <span className="bdraw-player-meta">
                  {formatPlayDateTime(recent.date, recent.time) || "—"}
                </span>
                <span className={`bdraw-player-status ${activityStatusTone(recent.status)}`}>
                  {activityStatusLabel(recent.status)}
                </span>
              </div>
              {hasRoundContext(recent, game) ? <RoundContext game={recent} /> : null}
              <div className="bdraw-investor-summary">
                <SummaryRow label="Investment" value={formatCoin(recent.investment)} />
                <SummaryRow label="Pool Size" value={formatCoin(recent.poolSize)} />
                <SummaryRow label="Your Share" value={`${getInvestorSharePercent(recent)}%`} valueClass="share" />
                {Number(recent.used || 0) || Number(recent.refunded || 0) ? (
                  <SummaryRow
                    label="Used / Refunded"
                    value={`${formatCoin(recent.used || 0)} / ${formatCoin(recent.refunded || 0)}`}
                  />
                ) : null}
              </div>
              <InvestorProfitCard recent={recent} formatCoin={formatCoin} />
            </>
          ) : (
            <p className="bdraw-player-empty-detail">No recent round activity yet.</p>
          )}

          {isCurrentUser && historyGames.length > 0 ? (
            <div className="bdraw-investor-history">
              <span className="bdraw-player-history-title">Recent Games</span>
              {historyGames.map((gameRow, idx) => (
                <InvestorGameHistoryCard
                  key={`ig-${idx}`}
                  game={gameRow}
                  gameType={game}
                  formatCoin={formatCoin}
                  RoundContext={RoundContext}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
};

export default BondPlayInvestorCard;
